import React, { useEffect, useState } from 'react'
import { Button, Modal, useToaster } from 'react-angle-dashboard-components'
import { Paths } from '../../routes/paths'
import { FieldPath, useFormContext } from 'react-hook-form'
import { useLocation, useHistory } from 'react-router-dom'
import { IQuoteFormData } from './QuoteForm'
import {
  useAttachAgency,
  useCreateAgentOfRecord,
  useCreateQuote,
  useUpdateQuote
} from '../../hooks/quote.hooks'
import {
  mapQuoteFormAddOnsSelectionToPatchDTO,
  mapQuoteFormForNetworkSelectionToPatchDTO,
  mapQuoteFormForPbmProgramSelectionToPatchDTO,
  mapQuoteFormToCreateDTO,
  mapQuoteFormToPatchDTO
} from '../../services/quote/mappers'
import FooterButtons from '../../components/FooterButtons/FooterButtons'

import { ReactComponent as ExclaimationImg } from '../../assets/exclaimationCircle.svg'

const navigationFlow = [
  Paths.basicInfoStep1,
  Paths.basicInfoStep2,
  Paths.basicInfoStep3,
  Paths.quoteFormPlanInfoStep1,
  Paths.quoteFormPlanInfoStep2,
  Paths.quoteFormCensusInfo,
  Paths.quoteFormQuoteDetailsStep1,
  Paths.quoteFormQuoteDetailsStep2,
  Paths.quoteFormQuoteReview
]

interface IFooterNavProps {
  onContinue?: (callback: (skipApi?: boolean) => void) => void
  onBack?: (callback: () => void) => void
  triggerPath: FieldPath<IQuoteFormData> | undefined
  isLoading?: boolean
  continueButtonText?: string
}

const FooterNav = ({
  onContinue: OnContinueOverride,
  onBack: onBackOverride,
  triggerPath,
  isLoading: isLoadingOverride,
  continueButtonText: continueButtonTextOverride
}: IFooterNavProps) => {
  const history = useHistory()
  const location = useLocation()
  const [showCancelModal, setShowCancelModal] = useState(false)
  const { showError } = useToaster()
  const { watch, trigger } = useFormContext<IQuoteFormData>()
  const quoteId = watch('entryId')?.toString()

  const { mutateAsync: createQuote, isLoading: isCreateLoading } = useCreateQuote()
  const { mutateAsync: createAgentOfRecord } = useCreateAgentOfRecord()
  const { mutateAsync: attachAgency } = useAttachAgency()
  const { mutateAsync: updateQuote, isLoading: isUpdateLoading } = useUpdateQuote()

  const [isLoading, setIsLoading] = useState(isCreateLoading || isUpdateLoading)

  const STEPS = {
    NETWORK_SELECTION: 1,
    PBM_SELECTION: 2,
    PLANS_SELECTION: 3,
    ADDONS_SELECTION: 4
  }

  useEffect(() => {
    if (isLoadingOverride !== undefined) {
      setIsLoading(isLoadingOverride)
    } else {
      setIsLoading(isCreateLoading || isUpdateLoading)
    }
  }, [isCreateLoading, isLoadingOverride, isUpdateLoading])

  const currentNavIndex = React.useMemo(
    () => navigationFlow.findIndex((path) => path() === location.pathname),
    [location.pathname]
  )

  const onCancel = () => {
    setShowCancelModal(true)
  }

  const onBack = () => {
    if (currentNavIndex !== undefined && currentNavIndex > 0) {
      const prevPath = navigationFlow[currentNavIndex - 1]
      history.push(prevPath(quoteId))
    }
  }

  const onBackClick = () => {
    if (onBackOverride) {
      onBackOverride(onBack)
    } else {
      onBack()
    }
  }

  const onContinue = async (skipApi: boolean = false) => {
    // submit api call to save piece of draft
    if (
      currentNavIndex !== undefined &&
      currentNavIndex < navigationFlow.length - 1
    ) {
      try {
        let navQuoteId = quoteId
        if (!skipApi) {
          if (!watch('entryId')) {
            const {
              agentOfRecordName,
              agentOfRecordEmailAddress,
              generalAgencyId,
              ...createQuoteDto
            } = watch('basicInfo.step1')
            // no id, create quote
            const res = await createQuote(
              mapQuoteFormToCreateDTO(createQuoteDto)
            )

            if (agentOfRecordName && agentOfRecordEmailAddress) {
              await createAgentOfRecord({
                quoteId: res.entryId,
                agentOfRecordName,
                agentOfRecordEmailAddress
              })
            }

            if (generalAgencyId) {
              await attachAgency({
                generalAgencyId,
                quoteId: res.entryId
              })
            }

            navQuoteId = res.entryId
          } else {
            if (quoteId) {
              switch (currentNavIndex) {
                case STEPS.NETWORK_SELECTION:
                  await updateQuote({
                    quoteId,
                    data: mapQuoteFormForNetworkSelectionToPatchDTO(watch())
                  })
                  break
                case STEPS.PBM_SELECTION:
                  await updateQuote({
                    quoteId,
                    data: mapQuoteFormForPbmProgramSelectionToPatchDTO(watch())
                  })
                  break
                case STEPS.PLANS_SELECTION:
                  // Do not send the quote patch
                  // request on plan selection
                  break;
                case STEPS.ADDONS_SELECTION:
                  await updateQuote({
                    quoteId,
                    data: mapQuoteFormAddOnsSelectionToPatchDTO(watch())
                  })
                  break
                default:
                  await updateQuote({
                    quoteId,
                    data: mapQuoteFormToPatchDTO(watch())
                  })
                  break;
              }
            } else {
              throw new Error('quoteId missing')
            }
          }
        }
        const nextPath = navigationFlow[currentNavIndex + 1]
        history.push(nextPath(navQuoteId))
      } catch (err) {
        console.error(err)
        showError(
          'There was an error saving the quote progress. Please check your entries and try again.'
        )
      }
    }
  }

  const onContinueClick = () => {
    trigger(triggerPath).then((validationPassed) => {
      if (validationPassed) {
        if (OnContinueOverride) {
          OnContinueOverride(onContinue)
        } else {
          onContinue()
        }
      } else {
        showError(
          'There was an error validating your entered info. Please check your entries and try again.'
        )
      }
    })
  }

  return (
    <>
      <Modal
        show={showCancelModal}
        centered
        style={{
          zIndex: 9999
        }}
        onHide={() => setShowCancelModal(false)}
      >
        <Modal.Header closeButton>Cancel changes</Modal.Header>
        <Modal.Body
          style={{
            whiteSpace: 'pre-wrap',
            textAlign: 'center'
          }}
        >
          <ExclaimationImg
            fill="#EDCA60"
            style={{
              width: 36,
              height: 36,
              overflow: 'visible',
              marginBottom: 20
            }}
          />
          <div>
            Are you sure you want to cancel?{'\n'}Changes on the current screen
            may be lost.
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            onClick={() => setShowCancelModal(false)}
            variant="outline-primary"
          >
            Close
          </Button>
          <div>
            <Button variant="danger" onClick={() => history.push(Paths.quotes)}>
              Cancel
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <FooterButtons
        onCancel={onCancel}
        onBack={onBackClick}
        onContinue={onContinueClick}
        hideBackButton={currentNavIndex === 0}
        isContinueDisabled={isLoading}
        continueButtonText={continueButtonTextOverride ?? 'Continue'}
        isLoading={isLoading}
      />
    </>
  )
}

export default FooterNav
